'use client';

import { useState, useRef, useLayoutEffect, type FormEvent } from 'react';
import Image from 'next/image';
import { Mail, Phone } from 'lucide-react';
import { gsap } from 'gsap';
import FormConsentCheckboxes from '@/components/forms/FormConsentCheckboxes';
import { isTradeEnquiryFormComplete } from '@/components/forms/formValidation.utils';
import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import { useRecaptcha } from '@/hooks/useRecaptcha';
import { RECAPTCHA_ACTIONS } from '@/lib/recaptcha-actions';
import { getStrapiFormatUrl } from '@/lib/strapi-media-url';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import type { CompanyProfileTradeEnquiries } from '@/types/company-profile';

gsap.registerPlugin(ScrollTrigger);

interface CompanyProfileTradeEnquiryProps {
  locale: string;
  zone: CompanyProfileTradeEnquiries;
}

type TradeStat = { value: string; label: string };

function buildHeadingParts(heading: string): string[] {
  return heading.split(/(?<=\.)\s+/).filter(Boolean);
}

function buildStats(rawStats: CompanyProfileTradeEnquiries['stats']): TradeStat[] {
  return (rawStats ?? [])
    .map((stat) => ({
      value:
        `${(stat.statValue ?? '').toString().trim()}${(stat.statSuffix ?? '').toString().trim()}`.trim(),
      label: (stat.statheading ?? stat.statDescription ?? '').toString().trim(),
    }))
    .filter((s) => s.value || s.label);
}

function renderContactIcon(iconUrl: string | null, label: string): React.ReactNode {
  if (iconUrl) {
    return <Image src={iconUrl} alt="" width={14} height={14} className="h-3.5 w-3.5 shrink-0" />;
  }
  if (label.includes('@')) {
    return <Mail className="h-3.5 w-3.5 shrink-0 text-[#ed1c22]" />;
  }
  return <Phone className="h-3.5 w-3.5 shrink-0 text-[#ed1c22]" />;
}

function renderContactRow(
  contact: CompanyProfileTradeEnquiries['contactInfo'][number],
): React.ReactNode {
  const iconUrl = getStrapiFormatUrl(contact.media);
  const href = contact.urlOptional?.trim();
  const label = contact.heading?.trim() || '';
  const iconEl = renderContactIcon(iconUrl, label);
  const content = (
    <div data-te-meta className="flex items-center gap-3" style={{ opacity: 0 }}>
      {iconEl}
      <p className="text-[13px] text-white/45">{label}</p>
    </div>
  );

  if (href) {
    return (
      <a key={contact.id} href={href} className="block transition-opacity hover:opacity-80">
        {content}
      </a>
    );
  }
  return <div key={contact.id}>{content}</div>;
}

function renderHeadingLines(
  headingParts: string[],
  fallbackHeading: string | null | undefined,
): React.ReactNode {
  if (headingParts.length > 0) {
    return headingParts.map((part, index) => (
      <span key={part} className="block overflow-hidden pb-[4px]">
        <span
          className={`block ${index === headingParts.length - 1 && headingParts.length > 1 ? 'text-white/18' : ''}`}
          data-te-line
        >
          {part}
        </span>
      </span>
    ));
  }
  return (
    <span className="block overflow-hidden pb-[4px]">
      <span className="block" data-te-line>
        {fallbackHeading}
      </span>
    </span>
  );
}

async function postTradeEnquiry(params: {
  email: string;
  message: string;
  privacyConsent: boolean;
  locale: string;
  recaptchaToken: string | null;
}): Promise<boolean> {
  const res = await fetch('/api/enquiry/send', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: params.email.trim(),
      message: params.message.trim(),
      consent: params.privacyConsent,
      locale: params.locale,
      sourceUrl: typeof window !== 'undefined' ? window.location.href : undefined,
      recaptchaToken: params.recaptchaToken ?? undefined,
    }),
  });
  return res.ok;
}

export default function CompanyProfileTradeEnquiry({
  locale,
  zone,
}: CompanyProfileTradeEnquiryProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const formWrap = zone.tradeForm?.tradeFormWrap?.[0];
  const emailField = formWrap?.inputField?.[0];
  const messageField = formWrap?.textArea?.[0];
  const privacyCheckbox = formWrap?.checkbox?.find(
    (field) => field.checkboxLabel?.trim().toLowerCase() === 'consent',
  );
  const submitLabel = formWrap?.submitButton?.buttonText?.trim() || 'Send Enquiry';

  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const {
    executeRecaptcha,
    isConfigured: isRecaptchaConfigured,
    isLoaded: isRecaptchaLoaded,
  } = useRecaptcha();

  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: { trigger: section, start: 'top 78%', toggleActions: 'play none none none' },
      });
      tl.fromTo(
        '[data-te-eyebrow]',
        { autoAlpha: 0, y: 8 },
        { autoAlpha: 1, y: 0, duration: 0.4, ease: 'power2.out' },
      )
        .fromTo(
          '[data-te-line]',
          { y: '110%' },
          { y: '0%', stagger: 0.1, duration: 0.75, ease: 'power3.out' },
          '-=0.2',
        )
        .fromTo(
          '[data-te-rule]',
          { scaleX: 0, transformOrigin: 'left center' },
          { scaleX: 1, duration: 0.5, ease: 'power2.inOut' },
          '-=0.4',
        )
        .fromTo(
          '[data-te-meta]',
          { autoAlpha: 0, x: -12 },
          { autoAlpha: 1, x: 0, stagger: 0.07, duration: 0.4, ease: 'power2.out' },
          '-=0.3',
        )
        .fromTo(
          '[data-te-right]',
          { autoAlpha: 0, x: 24 },
          { autoAlpha: 1, x: 0, duration: 0.65, ease: 'power2.out' },
          '-=0.4',
        );
    }, section);
    return () => ctx.revert();
  }, []);

  const canSubmit = isTradeEnquiryFormComplete({ email, message, privacyConsent });

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit || status === 'sending') return;
    setStatus('sending');
    try {
      const recaptchaToken = await executeRecaptcha(RECAPTCHA_ACTIONS.tradeEnquiry);
      const ok = await postTradeEnquiry({ email, message, privacyConsent, locale, recaptchaToken });
      if (!ok) throw new Error('Enquiry send failed');
      setStatus('sent');
      setEmail('');
      setMessage('');
      setPrivacyConsent(false);
    } catch {
      setStatus('error');
    }
  };

  const headingParts = buildHeadingParts(
    zone.heading ?? 'Partner with Wonder Cement. Build something great.',
  );

  const contacts = zone.contactInfo ?? [];
  const stats = buildStats(zone.stats);

  return (
    <section
      ref={sectionRef}
      className="grid grid-cols-1 lg:grid-cols-2 scroll-mt-[var(--site-header-height,5rem)]"
      id="trade-enquiries"
    >
      <div className="flex flex-col justify-between bg-[#0a0a0a] px-4 py-10 sm:px-10 sm:py-12 md:py-14 lg:px-14">
        <div>
          {zone.eyebrowHeading ? (
            <div
              data-te-eyebrow
              className="mb-7 inline-flex items-center gap-2.5"
              style={{ opacity: 0 }}
            >
              <span className="h-px w-5 bg-[#ed1c22]" aria-hidden />
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#ed1c22]">
                {zone.eyebrowHeading}
              </p>
            </div>
          ) : null}

          <h2 className="text-[1.75rem] font-bold leading-[1.06] tracking-tight text-white sm:text-[2.6rem]">
            {renderHeadingLines(headingParts, zone.heading)}
          </h2>

          <span
            data-te-rule
            aria-hidden
            className="mt-6 block h-[2px] w-10 rounded-full bg-[#ed1c22]"
            style={{ transform: 'scaleX(0)', transformOrigin: 'left center' }}
          />
        </div>

        <div className="mt-10 space-y-8 sm:mt-12">
          {contacts.length > 0 ? (
            <div className="space-y-3">{contacts.map((contact) => renderContactRow(contact))}</div>
          ) : null}

          {stats.length > 0 ? (
            <div
              data-te-meta
              className="grid grid-cols-3 gap-3 border-t border-white/[0.07] pt-6 sm:gap-4 sm:pt-7"
              style={{ opacity: 0 }}
            >
              {stats.map((s) => (
                <div key={s.label || s.value}>
                  <p className="text-base font-bold text-white sm:text-[1.1rem]">{s.value}</p>
                  {s.label ? (
                    <p className="mt-0.5 text-[9px] font-semibold uppercase tracking-[0.08em] text-white/25 sm:text-[10px] sm:tracking-[0.1em]">
                      {s.label}
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div
        data-te-right
        className="bg-white px-4 py-10 sm:px-10 sm:py-12 md:py-14 lg:px-14"
        style={{ opacity: 0 }}
      >
        {status === 'sent' ? (
          <div className="flex h-full flex-col justify-center gap-5">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#ed1c22]">
              <svg
                className="h-5 w-5 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </span>
            <div>
              <p className="text-lg font-bold text-black">Message sent.</p>
              <p className="mt-1 text-sm text-black/40">We&apos;ll respond within 24 hours.</p>
            </div>
            <button
              type="button"
              onClick={() => setStatus('idle')}
              className="w-fit text-[11px] font-semibold text-black/30 underline underline-offset-4 transition-colors hover:text-black"
            >
              Send another
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate className="flex h-full flex-col gap-7">
            <div className="mb-2">
              {zone.tradeForm?.eyebrowHeading ? (
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-black/35">
                  {zone.tradeForm.eyebrowHeading}
                </p>
              ) : null}
              {zone.tradeForm?.heading ? (
                <p className="mt-1 text-lg font-bold leading-snug tracking-tight text-black sm:text-[22px]">
                  {zone.tradeForm.heading}
                </p>
              ) : null}
            </div>

            <div>
              <label
                htmlFor={emailField?.inputId || 'cp-email'}
                className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.16em] text-black/40"
              >
                {emailField?.label || 'Email Address'}
              </label>
              <input
                id={emailField?.inputId || 'cp-email'}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required={emailField?.required ?? true}
                placeholder={emailField?.placeholder || 'you@company.com'}
                className="w-full rounded-xl border border-black/10 bg-[#f7f7f7] px-4 py-3.5 text-[14px] text-black placeholder-black/25 outline-none transition-all duration-200 focus:border-[#ed1c22] focus:ring-2 focus:ring-[#ed1c22]/10"
              />
            </div>

            <div className="flex-1">
              <label
                htmlFor={messageField?.textareaId || 'cp-message'}
                className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.16em] text-black/40"
              >
                {messageField?.label || 'Message'}
              </label>
              <textarea
                id={messageField?.textareaId || 'cp-message'}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                rows={4}
                placeholder={messageField?.placeholder || 'Tell us about your project…'}
                className="w-full resize-none rounded-xl border border-black/10 bg-[#f7f7f7] px-4 py-3.5 text-[14px] text-black placeholder-black/25 outline-none transition-all duration-200 focus:border-[#ed1c22] focus:ring-2 focus:ring-[#ed1c22]/10"
              />
            </div>

            {status === 'error' && (
              <p className="text-sm text-[#ed1c22]">Something went wrong. Try again.</p>
            )}

            <FormConsentCheckboxes
              idPrefix="cp-trade-enquiry"
              privacyConsent={privacyConsent}
              onPrivacyConsentChange={setPrivacyConsent}
              privacyHtml={privacyCheckbox?.checkboxText}
            />

            <SwapTextButton
              type="submit"
              text={status === 'sending' ? 'Submitting' : submitLabel}
              disabled={
                status === 'sending' ||
                !canSubmit ||
                Boolean(formWrap?.submitButton?.disabled) ||
                (isRecaptchaConfigured && !isRecaptchaLoaded)
              }
              loading={status === 'sending'}
              className="w-full justify-center"
            />
          </form>
        )}
      </div>
    </section>
  );
}
